import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { Todo, TodoPriority } from '../types';
import type { ListDestination } from '../services/todoListsApi';
import {
  saveToStorageAsync,
  loadFromStorageAsync,
  clearUserScopedStorageAsync,
  LAST_AUTH_UID_KEY,
} from '../services/storage';
import profileApi from '../services/profileApi';
import profileGraphqlApi from '../services/profileGraphqlApi';
import todosApi from '../services/todosApi';
import todoListsApi from '../services/todoListsApi';
import { TodoList } from '../types';
import { log } from '../utils/logger';
import { firstNameOf, normalizeRemoteName } from '../utils/name';
import { trackFeatureUsed } from '../services/analytics';
import { INTEGRATION_ERROR, retryIntegrationSync, syncIntegrations } from '../services/integrationsApi';
import { GraphqlResponseError, RequestTimeoutError, graphqlErrorCode } from '../services/graphqlClient';
import { applyIntegrationAccounts } from '../hooks/useIntegrations';
import { onAppRefresh } from '../services/appRefresh';
import { isNativeApp } from '../utils/nativeApp';
import {
  fromBackendTodo,
  isLocalId,
  isTmpListId,
  mergeServerTodos,
  reconcileServerLists,
  pendingTodoIds,
} from './todoMerge';
import type { BackendTodo } from '../services/todosApi';
import {
  addAppleReminder,
  appleTodoLists,
  appleTodos,
  deleteAppleReminder,
  getAppleRemindersSnapshot,
  hideAppleReminderList,
  installAppleReminders,
  reloadAppleReminders,
  isAppleId,
  resetAppleReminders,
  setAppleReminderCompleted,
  subscribeAppleReminders,
  updateAppleReminder,
} from './appleReminders';
import { resetAppleSettings } from '../features/Calendar/appleSettings';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

// ── Offline pending-queue ─────────────────────────────────────────────────────
// Mutations that fail while offline are stored here and replayed on reconnect.
// retries tracks how many times this mutation has failed; after MAX_MUTATION_RETRIES
// the entry is discarded so the queue never grows unbounded.
const MAX_MUTATION_RETRIES = 5;

type PendingMutation =
  | { type: 'upsert'; input: Parameters<typeof todosApi.upsertTodo>[0]; retries?: number }
  | { type: 'delete'; id: string; retries?: number };

const PENDING_KEY = 'pendingTodoMutations';

// ── Preference conflict resolution ────────────────────────────────────────────
// Tracks when this device last wrote preferences so remote wins only if newer.
const PREFS_LOCAL_UPDATED_AT_KEY = 'prefsLocalUpdatedAt';

function stampPrefsUpdatedAt(): void {
  void saveToStorageAsync(PREFS_LOCAL_UPDATED_AT_KEY, new Date().toISOString());
}

async function loadPendingQueue(): Promise<PendingMutation[]> {
  return loadFromStorageAsync<PendingMutation[]>(PENDING_KEY, []);
}

async function savePendingQueue(queue: PendingMutation[]): Promise<void> {
  return saveToStorageAsync(PENDING_KEY, queue);
}

// Serialized queue writes. Enqueues from one synchronous burst (a task tree,
// a completion cascade) otherwise all read the same queue snapshot and the
// last save wins — silently dropping every other offline mutation.
let pendingQueueChain: Promise<void> = Promise.resolve();
function enqueuePending(mutation: PendingMutation): void {
  pendingQueueChain = pendingQueueChain
    .then(async () => {
      const q = await loadPendingQueue();
      await savePendingQueue([...q, mutation]);
    })
    .catch((e) => log('Failed to enqueue pending mutation:', e as Error));
}

function queueUpsertMutation(input: Parameters<typeof todosApi.upsertTodo>[0]): void {
  enqueuePending({ type: 'upsert', input });
}

async function replayPendingQueue(): Promise<void> {
  if (!todosApi.isTodosApiConfigured()) return;
  // Ride the same chain as enqueuePending — a replay that loads the queue
  // while an enqueue is mid-write would resurrect or drop mutations.
  const chained = pendingQueueChain.then(() => replayPendingQueueInner());
  pendingQueueChain = chained.catch(() => undefined);
  return chained;
}

async function replayPendingQueueInner(): Promise<void> {
  const queue = await loadPendingQueue();
  if (!queue.length) return;

  const remaining: PendingMutation[] = [];
  for (const mutation of queue) {
    try {
      if (mutation.type === 'upsert') {
        await todosApi.upsertTodo(mutation.input);
      } else {
        await todosApi.deleteTodo(mutation.id);
      }
    } catch {
      const retries = (mutation.retries ?? 0) + 1;
      if (retries >= MAX_MUTATION_RETRIES) {
        log(`[AppContext] Dropping pending mutation after ${retries} failures`, mutation.type);
      } else {
        remaining.push({ ...mutation, retries }); // keep failed ones for next retry
      }
    }
  }
  await savePendingQueue(remaining);
}
// ─────────────────────────────────────────────────────────────────────────────

export type OverlayKey =
  | 'todos'
  | 'notes'
  | 'favorites'
  | 'qibla'
  | 'insights'
  | 'prayer'
  | 'profile'
  | 'weather'
  | 'search'
  | 'streak'
  // First-run gate (design spec §1). Not a panel — an onboarding surface that
  // claims the overlay slot so exactly one onboarding surface is on screen at a
  // time, and so ExploreTourProvider (which derives `suspended` from
  // `activeOverlay !== null`) holds the tour while it is up.
  | 'locationPrimer'
  // The one-time 1.1.0 meeting-permission notice. Same reason: it paints at
  // z 1199 over everything, so anything else that wants the slot must wait.
  | 'meetingNotice'
  // Calendars & tasks dialog (plans/connections-design.md §3). Claiming the
  // slot suspends the Explore Tour and keeps anything else from stacking.
  | 'connections';

/** Why an optimistic list's create failed (contract §1.1 codes, grouped by what the user can do). */
export type ListCreateError =
  | 'reauth'       // NEEDS_REAUTH: the account needs reconnecting
  | 'scope'        // SCOPE_REQUIRED (grant: tasks): same fix, reconnect
  | 'limit'        // PROVIDER_REJECTED, reason limit: retrying can't help
  | 'rejected'     // PROVIDER_REJECTED, reason rejected
  | 'invalid'      // BAD_USER_INPUT (e.g. the 50-linked-lists cap)
  | 'unavailable'  // PROVIDER_UNAVAILABLE, timeout, network: Retry is safe (C22 adopts)
  | 'other';

/**
 * The create of an optimistic (`tmp-`) list. No entry for a `tmp-` list means
 * the create is not running and never succeeded (e.g. stranded by a reload):
 * treat it as failed.
 */
export type ListCreateState =
  | { status: 'pending'; startedAt: number }
  | { status: 'failed'; error: ListCreateError };

interface AppContextType {
  name: string;
  setName: (name: string) => void;
  /** True once the user has typed their own name; derivation must never overwrite it. */
  nameIsCustom: boolean;
  focus: string;
  setFocus: (focus: string) => void;
  focusCompleted: boolean;
  setFocusCompleted: (completed: boolean) => void;
  prayerCalculationMethod: number;
  setPrayerCalculationMethod: (method: number) => void;
  weatherUnit: 'F' | 'C';
  setWeatherUnit: (unit: 'F' | 'C') => void;
  todos: Todo[];
  /**
   * Create one task. `opts.listId` files it into a list other than the active
   * one (the compose bar's "Save to" destination); omitted → the active list.
   */
  addTodo: (text: string, priority?: TodoPriority, blockKey?: string, opts?: { listId?: string }) => void;
  /** Creates a task with sub-tasks (one level) into a specific list — the
   * notes action-items path. Awaits the parent's backend id so children can
   * reference it; degrades to flat local adds when the backend is unreachable. */
  addTodoTree: (
    parent: { text: string; priority?: TodoPriority; blockKey?: string },
    subtasks: string[],
    opts?: { listId?: string },
  ) => Promise<void>;
  /** Adds one step under an existing task (drawer "+ Add a step"). No-op on a
   * parent that is itself a step or that only exists locally (unsynced). */
  addSubtask: (parentId: string, text: string) => void;
  toggleTodoCompleted: (id: string) => void;
  updateTodo: (id: string, text: string, priority?: TodoPriority) => void;
  setTodoBlock: (id: string, blockKey?: string) => void;
  deleteTodo: (id: string) => void;
  todoLists: TodoList[];
  activeListId: string;
  setActiveListId: (id: string) => void;
  /**
   * Optimistic: the list appears and becomes active at once. Never throws —
   * on failure the optimistic list stays (with its tasks) and its entry in
   * `listCreates` says why; resolves with the server list, or the optimistic
   * one on failure. `destination` creates it at Google Tasks / Todoist (C22).
   */
  createList: (name: string, destination?: ListDestination) => Promise<TodoList>;
  /** Create state of optimistic lists, keyed by their `tmp-` id. */
  listCreates: Readonly<Record<string, ListCreateState>>;
  /** Re-run a failed create with the list's current name and destination. */
  retryListCreate: (id: string) => void;
  /** Give up on the provider: create it as a plain Rhemn list, tasks included. */
  keepListInRhemn: (id: string) => void;
  renameList: (id: string, name: string) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  /**
   * Connected task apps (contract §7a): syncIntegrations → (if changed) the
   * lists + a complete todosPage walk → merge. Throttled to once a minute and
   * a no-op without synced lists unless `force`. `skipSync` re-reads without
   * asking the providers first (after setLinkedTaskLists / moveTodo).
   */
  refreshFromServer: (opts?: { force?: boolean; skipSync?: boolean }) => Promise<void>;
  /** F5: retry errored rows of an account (all, or just `todoIds`). */
  retryTodoSync: (accountId: string, todoIds?: string[]) => Promise<void>;
  /** Move a top-level task (and its steps) to another list (contract §7b). */
  moveTodo: (id: string, listId: string) => void;
  /** Native only: Reminders access was turned off in Settings (spec §7.5). */
  appleRemindersOff: boolean;
  // Single source of truth: only one overlay may be open at a time.
  activeOverlay: OverlayKey | null;
  openOverlay: (key: OverlayKey) => void;
  closeOverlay: (key?: OverlayKey) => void;
  /** Tasks drawer view (Barakah Blocks): flat list vs the day's prayer blocks. */
  todosView: 'list' | 'today';
  setTodosView: (view: 'list' | 'today') => void;
  /** Open the Tasks drawer, optionally forcing a view (Now strip/ribbon → 'today'). */
  openTodos: (view?: 'list' | 'today') => void;
  /**
   * Focus-session bridge: the drawer's per-task Focus buttons live far from the
   * NowBlock that mounts FocusSession — requests flow through here.
   */
  focusRequest: { taskId?: string } | null;
  requestFocusSession: (taskId?: string) => void;
  clearFocusRequest: () => void;
  toggleOverlay: (key: OverlayKey) => void;
  // Back-compat: derived booleans + toggles routed through the single overlay state.
  showTodos: boolean;
  toggleShowTodos: () => void;
  showNotes: boolean;
  toggleShowNotes: () => void;
  showQibla: boolean;
  toggleQibla: () => void;
  showFavorites: boolean;
  toggleShowFavorites: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

const isPriority = (value: unknown): value is TodoPriority =>
  value === 'Critical' || value === 'High' || value === 'Medium' || value === 'Low';

const normalizeTodo = (todo: Partial<Todo>): Todo => ({
  id: String(todo.id || Date.now().toString()),
  text: String(todo.text || ''),
  completed: Boolean(todo.completed),
  priority: isPriority(todo.priority) ? todo.priority : 'Medium',
  listId: typeof todo.listId === 'string' ? todo.listId : undefined,
  blockKey: typeof todo.blockKey === 'string' ? todo.blockKey : undefined,
  parentId: typeof todo.parentId === 'string' ? todo.parentId : undefined,
  // Sync fields survive a reload from storage; anything malformed drops to
  // the defaults ('rhemn' / 'synced') rather than breaking the list.
  ...(todo.source === 'google' || todo.source === 'todoist' || todo.source === 'apple' ? { source: todo.source } : {}),
  ...(todo.syncState === 'pending' || todo.syncState === 'error' ? { syncState: todo.syncState } : {}),
  ...(typeof todo.dueDate === 'string' ? { dueDate: todo.dueDate } : {}),
  ...(typeof todo.integrationAccountId === 'string' ? { integrationAccountId: todo.integrationAccountId } : {}),
});

// Monotonic all-digit temp ids. Date.now() alone collides when a batch flow
// (voice confirm strip, keep-plan, action items) adds several rows in the same
// millisecond — colliding rows then get merged onto ONE backend id and the
// others resurface as duplicates after the next sync. Still digits-only so
// isLocalId() keeps recognizing unsynced rows.
let localIdSeq = 0;
const nextLocalId = (): string => `${Date.now()}${(localIdSeq++ % 1000).toString().padStart(3, '0')}`;

// isLocalId (digit-only temp ids) lives in ./todoMerge with the merge rules.

/** Client-side floor between focus-triggered refreshes (contract §7a step 1). */
const REFRESH_THROTTLE_MS = 60_000;

/**
 * Which edits reach the provider (contract §7a, §7c). blockKey is Rhemn-only
 * everywhere; priority is Rhemn-only on Google lists (Google has none). An
 * edit that touches only Rhemn-only fields must never flash as pending.
 */
const pushesToProvider = (todo: Todo, change: { text?: boolean; completed?: boolean; priority?: boolean }): boolean => {
  if (!todo.source || todo.source === 'rhemn' || todo.source === 'apple') return false;
  if (change.text || change.completed) return true;
  return Boolean(change.priority) && todo.source === 'todoist';
};

/** Contract §1.1 → what the create-failure line can offer. */
const classifyListCreateError = (err: unknown): ListCreateError => {
  if (err instanceof RequestTimeoutError) return 'unavailable';
  if (!(err instanceof GraphqlResponseError)) {
    // fetch() rejects with a TypeError when the network is down.
    return err instanceof TypeError ? 'unavailable' : 'other';
  }
  switch (err.code) {
    case INTEGRATION_ERROR.needsReauth: return 'reauth';
    case INTEGRATION_ERROR.scopeRequired: return 'scope';
    case INTEGRATION_ERROR.providerRejected: return err.extensions.reason === 'limit' ? 'limit' : 'rejected';
    case INTEGRATION_ERROR.providerUnavailable: return 'unavailable';
    case 'BAD_USER_INPUT': return 'invalid';
    default: return 'other';
  }
};

/** Adopt the server's copy of the provider-owned fields after a write (R5). */
const withServerFields = (todo: Todo, row: BackendTodo | null | undefined): Todo => {
  if (!row || typeof row.id !== 'string') return todo;
  const server = fromBackendTodo(row);
  return {
    ...todo,
    id: server.id,
    source: server.source,
    syncState: server.syncState,
    dueDate: server.dueDate,
    integrationAccountId: server.integrationAccountId,
  };
};
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const hydratedProfileUserRef = useRef<string | null>(null);
  const profileHydratedRef = useRef<boolean>(false);
  const lastSyncedProfileRef = useRef<string>('');
  const todosSyncedForUserRef = useRef<string | null>(null);
  const focusSyncedForUserRef = useRef<string | null>(null);
  const lastSyncedPrefsRef = useRef<string>('');
  // Identity guard state: the uid seen on the previous render (in-session
  // sign-out detection) and whether a purge invalidated the mount-time storage
  // reads that may still be in flight.
  const prevUidRef = useRef<string | null>(null);
  const staleUserPurgedRef = useRef<boolean>(false);
  // Uid whose remote profile hydration has settled (success OR failure). The
  // prefs push effect is gated on this so a freshly signed-in account's backend
  // is never overwritten with this device's pre-hydration local state — that
  // race is how account A's stale name could poison account B's profile.
  const [remoteProfileSettledUid, setRemoteProfileSettledUid] = useState<string | null>(null);

  // Replay queued mutations when the network comes back online
  useEffect(() => {
    const handleOnline = () => {
      replayPendingQueue().catch((e) => log('Failed to replay pending mutations:', e as Error));
    };
    window.addEventListener('online', handleOnline);
    // Also try on mount in case we're already online with a stale queue
    handleOnline();
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const [name, setNameState] = useState<string>('');
  // Provenance for `name`. False = derived (provider displayName / email prefix) and
  // free to be re-derived; true = the user typed it and it is now authoritative.
  const [nameIsCustom, setNameIsCustomState] = useState<boolean>(false);
  const [focus, setFocus] = useState<string>('');
  const [focusCompleted, setFocusCompletedState] = useState<boolean>(false);
  const hasLoadedFromStorage = React.useRef(false);
  // Settles once the persisted state is in React state. The server syncs
  // below wait on it: applied first, the storage read would land on top and
  // put the cached lists and todos back over the fresher server copy.
  // Resolved from an effect, i.e. after the commit, so the refs mirroring
  // state (todoListsRef, todosRef) already hold the hydrated values.
  const [hydration] = useState(() => {
    let resolve!: () => void;
    const promise = new Promise<void>(r => { resolve = r; });
    return { promise, resolve };
  });
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { if (hydrated) hydration.resolve(); }, [hydrated, hydration]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [prayerCalculationMethod, setPrayerCalculationMethodState] = useState<number>(2);
  const [weatherUnit, setWeatherUnitState] = useState<'F' | 'C'>('F');
  const [todoLists, setTodoLists] = useState<TodoList[]>([]);
  const [activeListId, setActiveListId] = useState<string>('');
  const [listCreates, setListCreates] = useState<Record<string, ListCreateState>>({});
  // Bumped to (re-)send local-only rows after a commit — see resendLocalTodos.
  const [resendTick, setResendTick] = useState(0);
  const requestResend = useCallback(() => setResendTick(n => n + 1), []);
  // Single source of truth for every overlay/panel/dropdown. Opening any overlay
  // implicitly closes whatever was open, so two can never overlap.
  const [activeOverlay, setActiveOverlay] = useState<OverlayKey | null>(null);

  const showTodos = activeOverlay === 'todos';
  const showNotes = activeOverlay === 'notes';
  const showQibla = activeOverlay === 'qibla';
  const showFavorites = activeOverlay === 'favorites';

  // Analytics fired only on the open transition for instrumented overlays.
  const OVERLAY_OPEN_ANALYTICS: Partial<Record<OverlayKey, () => void>> = {
    qibla: () => trackFeatureUsed('qibla'),
    insights: () => trackFeatureUsed('insights'),
  };

  const openOverlay = useCallback((key: OverlayKey) => {
    setActiveOverlay(prev => {
      if (prev !== key) OVERLAY_OPEN_ANALYTICS[key]?.();
      return key;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the active overlay. With no arg (or when the named overlay is the active
  // one) it clears state — so a component can safely close only itself without
  // stomping an overlay that already replaced it.
  const closeOverlay = useCallback((key?: OverlayKey) => {
    setActiveOverlay(prev => (key === undefined || key === prev ? null : prev));
  }, []);

  // ── Barakah Blocks: tasks-drawer view + focus-session bridge ────────────────
  // Last-used view persists so planners land on their plan and list people
  // never see a mode they didn't ask for.
  const [todosView, setTodosViewState] = useState<'list' | 'today'>('list');
  const [focusRequest, setFocusRequest] = useState<{ taskId?: string } | null>(null);

  useEffect(() => {
    void loadFromStorageAsync<'list' | 'today'>('todosViewMode', 'list').then((v) => {
      if (v === 'today' || v === 'list') setTodosViewState(v);
    });
  }, []);

  const setTodosView = useCallback((view: 'list' | 'today') => {
    setTodosViewState(view);
    void saveToStorageAsync('todosViewMode', view);
  }, []);

  const openTodos = useCallback((view?: 'list' | 'today') => {
    if (view) setTodosView(view);
    setActiveOverlay('todos');
  }, [setTodosView]);

  const requestFocusSession = useCallback((taskId?: string) => {
    setActiveOverlay(null); // the session is full-screen; the drawer steps aside
    setFocusRequest({ taskId });
  }, []);

  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);

  const toggleOverlay = useCallback((key: OverlayKey) => {
    setActiveOverlay(prev => {
      if (prev === key) return null;
      OVERLAY_OPEN_ANALYTICS[key]?.();
      return key;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load persisted values on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const storedName = await loadFromStorageAsync<string>('name', '');
        const storedNameIsCustom = await loadFromStorageAsync<boolean>('nameIsCustom', false);
        const storedFocus = await loadFromStorageAsync<string>('focus', '');
        const storedTodos = await loadFromStorageAsync<Partial<Todo>[]>('todos', []);
        const storedFocusCompleted = await loadFromStorageAsync<boolean>('focusCompleted', false);
        const storedPrayerMethod = await loadFromStorageAsync<number>('prayerCalculationMethod', 2);
        const storedWeatherUnit = await loadFromStorageAsync<'F' | 'C'>('weatherUnit', 'F');
        const storedLists = await loadFromStorageAsync<TodoList[]>('todoLists', []);
        const storedActiveListId = await loadFromStorageAsync<string>('activeListId', '');
        if (!mounted) return;
        // The identity guard purged user-scoped state while these reads were in
        // flight (account switch detected at mount) — the values above belong to
        // the previous owner; applying them would resurrect exactly the leak the
        // purge exists to prevent.
        if (staleUserPurgedRef.current) {
          hasLoadedFromStorage.current = true;
          setHydrated(true);
          return;
        }
        if (storedName) setNameState(storedName);
        if (storedNameIsCustom === true) setNameIsCustomState(true);
        if (storedFocus) setFocus(storedFocus);
        if (storedTodos && storedTodos.length) setTodos(storedTodos.map(normalizeTodo));
        if (storedLists && storedLists.length) {
          setTodoLists(storedLists);
          setActiveListId(storedActiveListId || storedLists[0].id);
        }
        if (typeof storedFocusCompleted === 'boolean') setFocusCompletedState(storedFocusCompleted);
        if (typeof storedPrayerMethod === 'number') setPrayerCalculationMethodState(storedPrayerMethod);
        if (storedWeatherUnit === 'F' || storedWeatherUnit === 'C') setWeatherUnitState(storedWeatherUnit);
        hasLoadedFromStorage.current = true;
        setHydrated(true);
      } catch (e) {
        log('Failed to load persisted app state:', e as Error);
        hasLoadedFromStorage.current = true;
        setHydrated(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Save focus whenever it changes — guard against overwriting stored value on initial mount
  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('focus', focus);
  }, [focus]);

  // Save name whenever it changes
  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('name', name);
  }, [name]);

  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('nameIsCustom', nameIsCustom);
  }, [nameIsCustom]);

  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('focusCompleted', focusCompleted);
  }, [focusCompleted]);

  // Save todos whenever they change (guard against the empty initial state
  // overwriting the stored value before hydration reads it — without this,
  // signed-out users' local todos are wiped on every page load).
  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('todos', todos);
    log('Saving todos to storage', { count: todos.length });
  }, [todos]);

  // Save lists whenever they change (guard against empty initial state overwriting stored value)
  useEffect(() => {
    if (todoLists.length > 0) void saveToStorageAsync('todoLists', todoLists);
  }, [todoLists]);

  useEffect(() => {
    if (activeListId) void saveToStorageAsync('activeListId', activeListId);
  }, [activeListId]);

  // Same guard as above: without it the initial defaults (method 2, °F) are
  // written over the stored choice before hydration's async read lands, so the
  // user's calculation method and unit reset on every launch.
  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('prayerCalculationMethod', prayerCalculationMethod);
  }, [prayerCalculationMethod]);

  useEffect(() => {
    if (!hasLoadedFromStorage.current) return;
    void saveToStorageAsync('weatherUnit', weatherUnit);
  }, [weatherUnit]);

  // Reset every piece of USER-scoped in-memory state to its initial value.
  // Called when the account that owns this state is no longer the signed-in
  // account (sign-out, or a different uid signing in). Device-scoped state
  // (theme, todos view mode, overlay UI) is intentionally untouched. The
  // per-uid sync refs reset too, so the next account hydrates from scratch.
  const resetUserScopedState = useCallback(() => {
    setNameState('');
    // Provenance is user-scoped too. Left set, a signed-out user's "I typed this
    // name" flag would carry into the next account signed in without a reload:
    // it suppresses name derivation, and the profile sync then writes the new
    // account's derived-or-empty name back as if the user had chosen it.
    setNameIsCustomState(false);
    setFocus('');
    setFocusCompletedState(false);
    setTodos([]);
    setTodoLists([]);
    setActiveListId('');
    setListCreates({});
    setPrayerCalculationMethodState(2);
    setWeatherUnitState('F');
    hydratedProfileUserRef.current = null;
    profileHydratedRef.current = false;
    lastSyncedProfileRef.current = '';
    todosSyncedForUserRef.current = null;
    focusSyncedForUserRef.current = null;
    lastSyncedPrefsRef.current = '';
    setRemoteProfileSettledUid(null);
    // Apple Calendar & Reminders choices and rows are user-scoped too.
    resetAppleSettings();
    resetAppleReminders();
  }, []);

  // ── Account identity guard ──────────────────────────────────────────────────
  // Identity-derived local state (greeting name, intention, cached todos, …)
  // must never survive an account switch: TestFlight build 6 greeted account B
  // with account A's name because the `name` key is device-global and sign-out
  // never touched it (yesterday's teardown cleared AUTH keys only). Contract:
  //   • sign-in with the SAME uid as the last owner → keep everything (resume);
  //   • sign-in with a DIFFERENT uid → purge user-scoped state (incl. the
  //     local-only streak) BEFORE the uid-keyed hydration effects below fetch
  //     the new account's own data;
  //   • sign-out → clear user-scoped state so the next viewer isn't greeted as
  //     the departed account, but keep the streak + owner marker so the same
  //     account can resume them on this device.
  // Declared ABOVE every backend-sync effect and read synchronously from
  // localStorage (storage.ts's primary store), so the purge completes before
  // those effects run in this same commit.
  useEffect(() => {
    const uid = user?.uid ?? null;
    const prevUidInSession = prevUidRef.current;
    prevUidRef.current = uid;

    if (!uid) {
      // Only a real sign-out transition — never the initial null while auth is
      // still resolving — may clear local data (anonymous users keep theirs).
      if (prevUidInSession) {
        staleUserPurgedRef.current = true;
        resetUserScopedState();
        void clearUserScopedStorageAsync();
      }
      return;
    }

    let lastOwnerUid: string | null = null;
    try {
      const raw = localStorage.getItem(LAST_AUTH_UID_KEY);
      lastOwnerUid = raw ? (JSON.parse(raw) as string) : null;
    } catch {
      lastOwnerUid = null;
    }

    // No previous owner (fresh device or anonymous-only usage): keep local
    // data so it migrates into this first account — same policy as the
    // local-todo migration in the todos sync below.
    if (lastOwnerUid && lastOwnerUid !== uid) {
      staleUserPurgedRef.current = true;
      resetUserScopedState();
      void clearUserScopedStorageAsync({ includeLocalOnly: true });
    }
    void saveToStorageAsync(LAST_AUTH_UID_KEY, uid);
  }, [user?.uid, resetUserScopedState]);

  // Sync todos with backend when user authenticates
  useEffect(() => {
    if (!user?.uid) {
      todosSyncedForUserRef.current = null;
      return;
    }
    if (!todosApi.isTodosApiConfigured()) return;
    if (todosSyncedForUserRef.current === user.uid) return;

    let cancelled = false;

    (async () => {
      try {
        // Complete, paginated walk (contract C14). `todos` is capped at 1,000
        // rows, and treating its truncation as deletions would silently drop
        // every task past the cap.
        const walk = await todosApi.walkAllTodos(() => !cancelled);
        if (cancelled) return;
        if (!walk.complete && walk.items.length === 0) {
          throw new Error('todosPage walk failed before the first page');
        }
        const pendingIds = pendingTodoIds(await loadPendingQueue());
        await hydration.promise;
        if (cancelled) return;
        const remoteTodos = walk.items;

        todosSyncedForUserRef.current = user.uid;

        // §7a merge: server rows win, pending + local-only rows stay, and a
        // missing row is a delete only if the walk was complete. Pure: the
        // local-only rows are sent AFTER the commit (resendLocalTodos), never
        // from inside this updater, which StrictMode and concurrent rendering
        // may invoke more than once.
        setTodos(prevLocalTodos => mergeServerTodos({
          local: prevLocalTodos,
          server: remoteTodos,
          complete: walk.complete,
          pendingIds,
        }).todos);
        requestResend();
      } catch (e) {
        log('Failed to sync todos from backend:', e as Error);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid, requestResend, hydration]);

  // ── List snapshots vs local list changes ───────────────────────────────────
  // A todoLists snapshot describes the server at the moment it was READ. A
  // list created (or deleted) here while that request was in flight is not in
  // it, and applying it "server wins" used to drop the brand-new list from the
  // menu until the next reload. Every local create/delete bumps an epoch; a
  // snapshot is reconciled against the changes made since it was requested.
  const listEpochRef = useRef(0);
  const listTouchesRef = useRef(new Map<string, { epoch: number; kind: 'created' | 'deleted' }>());
  const touchList = useCallback((id: string, kind: 'created' | 'deleted') => {
    listEpochRef.current += 1;
    listTouchesRef.current.set(id, { epoch: listEpochRef.current, kind });
  }, []);
  /** Optimistic list ids whose create request is running. */
  const listCreateInFlightRef = useRef(new Set<string>());
  const uidRef = useRef<string | null>(user?.uid ?? null);
  uidRef.current = user?.uid ?? null;

  const todosRef = useRef(todos);
  todosRef.current = todos;
  const todoListsRef = useRef(todoLists);
  todoListsRef.current = todoLists;

  /**
   * Apply a server todoLists snapshot requested at epoch `since` (see above).
   * Stranded optimistic lists the server turns out to have (a lost response)
   * are adopted: their tasks and the active list move to the server id.
   */
  const applyServerLists = useCallback((server: TodoList[], since: number) => {
    const createdSince = new Set<string>();
    const deletedSince = new Set<string>();
    for (const [id, touch] of listTouchesRef.current) {
      if (touch.epoch <= since) continue;
      (touch.kind === 'created' ? createdSince : deletedSince).add(id);
    }
    const opts = { createdSince, deletedSince, inFlight: listCreateInFlightRef.current };
    const { lists, adopted } = reconcileServerLists(todoListsRef.current, server, opts);
    setTodoLists(prev => reconcileServerLists(prev, server, opts).lists);
    if (adopted.size > 0) {
      setTodos(prev => prev.map(t => (t.listId && adopted.has(t.listId) ? { ...t, listId: adopted.get(t.listId) } : t)));
      setListCreates(prev => {
        const next = { ...prev };
        for (const id of adopted.keys()) delete next[id];
        return next;
      });
      requestResend();
    }
    setActiveListId(prev => {
      const moved = adopted.get(prev);
      if (moved) return moved;
      return lists.some(l => l.id === prev) || isTmpListId(prev) || isAppleId(prev) ? prev : (lists[0]?.id ?? prev);
    });
  }, [requestResend]);

  // Sync todo lists from backend on login — backend is the source of truth,
  // reconciled with what this client changed while the request was out.
  useEffect(() => {
    if (!user?.uid) return;
    if (!todoListsApi.isTodoListsApiConfigured()) return;
    let cancelled = false;

    (async () => {
      try {
        const since = listEpochRef.current;
        let remoteLists = await todoListsApi.getTodoLists();
        if (cancelled) return;

        // First login: create a default list if the backend has none
        if (remoteLists.length === 0) {
          const defaultList = await todoListsApi.createTodoList('New List');
          if (cancelled) return;
          remoteLists = [defaultList];
        }

        await hydration.promise;
        if (cancelled) return;
        applyServerLists(remoteLists, since);
      } catch (e) {
        log('Failed to sync todo lists from backend:', e as Error);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid, applyServerLists, hydration]);

  // ── Local-only rows (digit-only temp ids): create, and re-send on failure ──
  // Every create goes through sendCreate. The guard is keyed by temp id:
  //   absent → never sent, or the last attempt failed (eligible);
  //   null   → a request is in flight (skip);
  //   string → created; the server id it became (skip, and parent lookups).
  // Kept for the session, so a trigger that still sees the temp id in a
  // not-yet-re-rendered todosRef can never create the row a second time.
  const tempCreatesRef = useRef(new Map<string, string | null>());
  const tempFailuresRef = useRef(new Map<string, number>());

  const sendCreate = useCallback(async (row: Todo, parentId?: string): Promise<string | null> => {
    const guard = tempCreatesRef.current;
    if (guard.has(row.id)) return guard.get(row.id) ?? null;
    guard.set(row.id, null);
    const uid = uidRef.current;
    let backendTodo: BackendTodo;
    try {
      backendTodo = await todosApi.upsertTodo({
        text: row.text, completed: row.completed, priority: row.priority,
        listId: row.listId, blockKey: row.blockKey,
        ...(parentId ? { parentId } : {}),
      });
    } catch (e) {
      guard.delete(row.id);
      tempFailuresRef.current.set(row.id, (tempFailuresRef.current.get(row.id) ?? 0) + 1);
      log('Failed to sync new todo; will re-send on the next refresh:', e as Error);
      return null;
    }
    const realId = backendTodo.id;
    guard.set(row.id, realId);
    tempFailuresRef.current.delete(row.id);
    if (uidRef.current !== uid) return realId;

    // What the user did to the row while the create was out: every edit path
    // skips temp ids, so those edits exist only locally. Replay them.
    const current = todosRef.current.find(t => t.id === row.id);
    setTodos(prev => prev.map(t =>
      t.id === row.id ? withServerFields(t, backendTodo)
        : t.parentId === row.id ? { ...t, parentId: realId } : t));
    if (!current) {
      todosApi.deleteTodo(realId).catch(() => enqueuePending({ type: 'delete', id: realId }));
    } else if (current.text !== row.text || current.completed !== row.completed
      || current.priority !== row.priority || current.blockKey !== row.blockKey) {
      const input = { id: realId, text: current.text, completed: current.completed, priority: current.priority, blockKey: current.blockKey ?? null };
      todosApi.upsertTodo(input).catch(() => enqueuePending({ type: 'upsert', input }));
    }
    return realId;
  }, []);

  /**
   * Send every local-only row that isn't already in flight: parents first, so
   * a step can reference its parent's real id. Rows in an optimistic `tmp-`
   * list wait for that list's create (its success requests a resend).
   */
  const resendLocalTodos = useCallback(async (): Promise<void> => {
    if (!uidRef.current || !todosApi.isTodosApiConfigured()) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const guard = tempCreatesRef.current;
    const eligible = todosRef.current.filter(t =>
      isLocalId(t.id) && !guard.has(t.id) && !isTmpListId(t.listId)
      && (tempFailuresRef.current.get(t.id) ?? 0) < MAX_MUTATION_RETRIES);
    for (const row of eligible.filter(t => !t.parentId)) await sendCreate(row);
    for (const row of eligible.filter(t => t.parentId)) {
      const parent = row.parentId as string; // filtered on parentId above
      const parentId = isLocalId(parent) ? guard.get(parent) : parent;
      if (!parentId) continue; // parent not on the server yet: a later trigger
      await sendCreate(row, parentId);
    }
  }, [sendCreate]);

  useEffect(() => {
    if (resendTick > 0) void resendLocalTodos();
  }, [resendTick, resendLocalTodos]);

  // Triggers besides the merges: back online, and the tab/app coming forward.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') requestResend(); };
    window.addEventListener('online', requestResend);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', requestResend);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.uid, requestResend]);

  // ── Connected task apps: refreshFromServer (contract §7a) ──────────────────
  // Runs only when the user has at least one synced list: without one there
  // is nothing a provider could have changed. Serialized (one run at a time)
  // and throttled to once a minute unless forced.
  const hasSyncedLists = todoLists.some(l => Boolean(l.integrationAccountId) && !isTmpListId(l.id));
  const hasSyncedListsRef = useRef(hasSyncedLists);
  hasSyncedListsRef.current = hasSyncedLists;
  const lastRefreshAtRef = useRef(0);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  const refreshFromServer = useCallback(async (
    opts: { force?: boolean; skipSync?: boolean } = {},
  ): Promise<void> => {
    const uid = user?.uid;
    if (!uid || !todosApi.isTodosApiConfigured()) return;
    if (!opts.force && !hasSyncedListsRef.current) return;
    if (!opts.force && Date.now() - lastRefreshAtRef.current < REFRESH_THROTTLE_MS) return;
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    lastRefreshAtRef.current = Date.now();
    const run = (async () => {
      try {
        if (!opts.skipSync) {
          // Auth only (C10): a lapsed user gets `paused` accounts, changed:false.
          const res = await syncIntegrations();
          applyIntegrationAccounts(uid, res.accounts);
          // A list create that failed on the account's health (reconnect,
          // grant) becomes plainly retryable once the account is healthy.
          const healthy = new Set(res.accounts
            .filter(a => a.status === 'active' && (a.grants ?? []).includes('tasks'))
            .map(a => a.id));
          setListCreates(prev => {
            let next: Record<string, ListCreateState> | null = null;
            for (const [id, st] of Object.entries(prev)) {
              if (st.status !== 'failed' || (st.error !== 'reauth' && st.error !== 'scope')) continue;
              const accountId = todoListsRef.current.find(l => l.id === id)?.integrationAccountId;
              if (!accountId || !healthy.has(accountId)) continue;
              next = next ?? { ...prev };
              next[id] = { status: 'failed', error: 'other' };
            }
            return next ?? prev;
          });
          if (!res.changed && !opts.force) return;
        }
        const since = listEpochRef.current;
        const lists = await todoListsApi.getTodoLists();
        applyServerLists(lists, since);

        // Abandon the walk if the user leaves or drops offline: a partial walk
        // may upsert, but it must never delete (C14).
        const walk = await todosApi.walkAllTodos(() =>
          (typeof document === 'undefined' || document.visibilityState !== 'hidden')
          && (typeof navigator === 'undefined' || navigator.onLine !== false));
        const pendingIds = pendingTodoIds(await loadPendingQueue());
        setTodos(prev => {
          const merged = mergeServerTodos({ local: prev, server: walk.items, complete: walk.complete, pendingIds });
          return merged.changed ? merged.todos : prev;
        });
        requestResend();
      } catch (e) {
        log('refreshFromServer failed; will retry on the next trigger:', e as Error);
      }
    })().finally(() => {
      refreshInFlightRef.current = null;
    });
    refreshInFlightRef.current = run;
    return run;
  }, [user?.uid, applyServerLists, requestResend]);

  // Triggers: the tab/app becoming visible (throttled), and native
  // pull-to-refresh. "After a connect completes" calls refreshFromServer
  // directly (force) from the Connections surfaces.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshFromServer();
    };
    document.addEventListener('visibilitychange', onVisible);
    const offRefresh = isNativeApp() ? onAppRefresh(() => refreshFromServer({ force: true })) : null;
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      offRefresh?.();
    };
  }, [user?.uid, refreshFromServer]);

  /**
   * "Not synced" / status-line Retry (F5). Revives errored rows server-side and
   * adopts their fresh state; the rows usually come back `pending` → `synced`.
   */
  const retryTodoSync = useCallback(async (accountId: string, todoIds?: string[]): Promise<void> => {
    const rows = await retryIntegrationSync(accountId, todoIds);
    const byId = new Map(rows.map(r => [r.id, r]));
    setTodos(prev => prev.map(t => {
      const row = byId.get(t.id);
      return row ? withServerFields(t, row) : t;
    }));
  }, []);

  /**
   * Move a TOP-LEVEL task to another list (contract §7b). Its steps follow
   * locally and on the server. Moving a step on its own is not offered.
   * A move into, out of or across synced accounts re-creates the task at the
   * provider, so the row comes back pending or plain; adopt what the server
   * returns, then refresh so the steps pick up their new state too.
   */
  const moveTodo = useCallback((id: string, listId: string) => {
    // Apple Reminders never move through the server; a cross-app move isn't offered.
    if (isAppleId(id) || isAppleId(listId)) return;
    const target = todosRef.current.find(t => t.id === id);
    if (!target || target.parentId || target.listId === listId) return;
    setTodos(prev => prev.map(t => (t.id === id || t.parentId === id ? { ...t, listId } : t)));
    if (isLocalId(id) || isTmpListId(listId) || !todosApi.isTodosApiConfigured()) return;
    todosApi.moveTodo(id, listId)
      .then((row) => {
        setTodos(prev => prev.map(t => (t.id === id ? { ...withServerFields(t, row), listId: row.listId || listId } : t)));
        void refreshFromServer({ force: true, skipSync: true });
      })
      .catch((e) => {
        log('moveTodo failed; reconciling from the server:', e as Error);
        void refreshFromServer({ force: true, skipSync: true });
      });
  }, [refreshFromServer]);

  // ── Lists — backend-first, optimistic ──────────────────────────────────────
  /**
   * Run (or re-run) the create of optimistic list `tempId`. One request per
   * list at a time. Success swaps the list, the active id and its tasks to the
   * server id, then sends the tasks typed meanwhile. Failure keeps everything
   * and records why in `listCreates` (the status line reads it).
   */
  const runListCreate = useCallback(async (
    tempId: string,
    name: string,
    destination?: ListDestination,
  ): Promise<TodoList | null> => {
    const inFlight = listCreateInFlightRef.current;
    if (inFlight.has(tempId)) return null;
    inFlight.add(tempId);
    const uid = uidRef.current;
    setListCreates(prev => ({ ...prev, [tempId]: { status: 'pending', startedAt: Date.now() } }));
    try {
      let remote: TodoList;
      try {
        remote = await todoListsApi.createTodoList(name, destination);
      } catch (e) {
        // C22: a Pro user's account can still read `paused` until the resume
        // runs. syncIntegrations(accountId) resumes it; then retry, once.
        if (!destination?.accountId || graphqlErrorCode(e) !== INTEGRATION_ERROR.accountPaused) throw e;
        const res = await syncIntegrations(destination.accountId);
        if (uid) applyIntegrationAccounts(uid, res.accounts);
        remote = await todoListsApi.createTodoList(name, destination);
      }
      if (uidRef.current !== uid) return null;
      touchList(remote.id, 'created');
      setListCreates(prev => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });
      if (listTouchesRef.current.get(tempId)?.kind === 'deleted') {
        // Deleted while the create was out: remove what the server just made.
        todoListsApi.deleteTodoList(remote.id).catch(e => log('Failed to delete a list removed mid-create:', e as Error));
        return null;
      }
      setTodoLists(prev => (prev.some(l => l.id === tempId)
        ? prev.map(l => (l.id === tempId ? remote : l))
        : prev.some(l => l.id === remote.id) ? prev : [...prev, remote]));
      setActiveListId(curr => (curr === tempId ? remote.id : curr));
      setTodos(prev => prev.map(t => (t.listId === tempId ? { ...t, listId: remote.id } : t)));
      requestResend();
      return remote;
    } catch (e) {
      log('Failed to create list:', e as Error);
      if (uidRef.current === uid) {
        setListCreates(prev => ({ ...prev, [tempId]: { status: 'failed', error: classifyListCreateError(e) } }));
      }
      return null;
    } finally {
      inFlight.delete(tempId);
    }
  }, [touchList, requestResend]);

  const createList = useCallback(async (name: string, destination?: ListDestination): Promise<TodoList> => {
    const finalName = name.trim() || 'New List';
    const tempId = `tmp-${nextLocalId()}`;
    const now = new Date().toISOString();
    // A provider list carries its provider from the first frame, so the mark
    // renders before the server answers (spec §9).
    const optimistic: TodoList = {
      id: tempId,
      userId: user?.uid ?? '',
      name: finalName,
      createdAt: now,
      updatedAt: now,
      ...(destination
        ? { source: destination.provider, provider: destination.provider, integrationAccountId: destination.accountId ?? null }
        : {}),
    };
    setTodoLists(prev => [...prev, optimistic]);
    setActiveListId(tempId);
    if (!todoListsApi.isTodoListsApiConfigured()) return optimistic;
    return (await runListCreate(tempId, finalName, destination)) ?? optimistic;
  }, [user?.uid, runListCreate]);

  const retryListCreate = useCallback((id: string) => {
    const list = todoListsRef.current.find(l => l.id === id);
    if (!list || !isTmpListId(id)) return;
    const provider = list.provider === 'google' || list.provider === 'todoist' ? list.provider : null;
    void runListCreate(id, list.name, provider
      ? { provider, ...(list.integrationAccountId ? { accountId: list.integrationAccountId } : {}) }
      : undefined);
  }, [runListCreate]);

  const keepListInRhemn = useCallback((id: string) => {
    const list = todoListsRef.current.find(l => l.id === id);
    if (!list || !isTmpListId(id) || listCreateInFlightRef.current.has(id)) return;
    setTodoLists(prev => prev.map(l => (l.id === id
      ? { ...l, source: 'rhemn', provider: null, integrationAccountId: null }
      : l)));
    void runListCreate(id, list.name, undefined);
  }, [runListCreate]);

  const renameList = useCallback(async (id: string, name: string): Promise<void> => {
    // Reminders lists are renamed in Reminders, not here.
    if (isAppleId(id)) return;
    const trimmed = name.trim() || 'New List';
    setTodoLists(prev => prev.map(l => l.id === id ? { ...l, name: trimmed, updatedAt: new Date().toISOString() } : l));
    // An optimistic list has no server row yet; Retry sends the current name.
    if (isTmpListId(id)) return;
    todoListsApi.renameTodoList(id, trimmed).catch(e => log('Failed to sync list rename:', e as Error));
  }, []);

  const deleteList = useCallback(async (id: string): Promise<void> => {
    // A Reminders list: "Remove from Rhemn" only stops showing it here.
    if (isAppleId(id)) {
      hideAppleReminderList(id);
      setActiveListId(curr => (curr === id ? (todoListsRef.current[0]?.id ?? '') : curr));
      return;
    }
    touchList(id, 'deleted');
    // Side effects stay OUT of the updaters: StrictMode double-invokes them,
    // which used to create two default lists after deleting the last one.
    const remaining = todoListsRef.current.filter(l => l.id !== id);
    setTodoLists(prev => prev.filter(l => l.id !== id));
    setActiveListId(curr => (curr === id ? (remaining[0]?.id ?? '') : curr));
    setTodos(prev => prev.filter(t => t.listId !== id));
    setListCreates(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (remaining.length === 0) {
      // Last list deleted — create a new default on the backend
      todoListsApi.createTodoList('New List')
        .then(newList => {
          touchList(newList.id, 'created');
          setTodoLists([newList]);
          setActiveListId(newList.id);
        })
        .catch(e => log('Failed to create default list after deleting last:', e as Error));
    }
    // An optimistic list has no server row; an in-flight create sees the
    // 'deleted' touch and removes what it made.
    if (isTmpListId(id)) return;
    todoListsApi.deleteTodoList(id).catch(e => log('Failed to sync list delete:', e as Error));
  }, [touchList]);

  // Todo functions
  const addTodo = useCallback((
    text: string,
    priority: TodoPriority = 'Medium',
    blockKey?: string,
    opts?: { listId?: string },
  ) => {
    if (text.trim() === '') return;
    // Same resolution as addTodoTree: an explicit destination, else the active list.
    const target = opts?.listId || activeListId;
    // Apple Reminders: straight to EventKit, never the server or the queue.
    if (isAppleId(target)) {
      addAppleReminder(target, text, priority);
      trackFeatureUsed('tasks');
      return;
    }

    const tempId = nextLocalId();
    const listId = target || undefined;
    const newTodo: Todo = { id: tempId, listId, text, completed: false, priority, blockKey };

    setTodos(prevTodos => [...prevTodos, newTodo]);
    log('Added todo', { id: tempId });
    trackFeatureUsed('tasks');

    // A task typed into a list whose create is still out waits for the list's
    // real id (runListCreate requests the resend).
    if (!todosApi.isTodosApiConfigured() || isTmpListId(listId)) return;
    void sendCreate(newTodo);
  }, [activeListId, sendCreate]);

  const addTodoTree = useCallback(async (
    parent: { text: string; priority?: TodoPriority; blockKey?: string },
    subtasks: string[],
    opts?: { listId?: string },
  ): Promise<void> => {
    const text = parent.text.trim();
    if (!text) return;
    const priority = parent.priority ?? 'Medium';
    const listId = opts?.listId ?? activeListId ?? undefined;
    const cleanSubs = subtasks.map((sub) => sub.trim()).filter(Boolean);
    // Reminders has no steps here: the task and its steps land as reminders.
    if (isAppleId(listId)) {
      addAppleReminder(listId as string, text, priority);
      for (const sub of cleanSubs) addAppleReminder(listId as string, sub, 'Medium');
      trackFeatureUsed('tasks');
      return;
    }

    // Optimistic: parent + children appear immediately (temp ids).
    const parentRow: Todo = { id: nextLocalId(), listId, text, completed: false, priority, blockKey: parent.blockKey };
    const childRows: Todo[] = cleanSubs.map((sub) => ({
      id: nextLocalId(), listId, text: sub, completed: false,
      priority: 'Medium' as TodoPriority, parentId: parentRow.id,
    }));
    setTodos(prev => [...prev, parentRow, ...childRows]);
    trackFeatureUsed('tasks');
    if (cleanSubs.length > 0) trackFeatureUsed('task_steps');

    if (!todosApi.isTodosApiConfigured() || isTmpListId(listId)) return;

    // The children need the parent's REAL id, so the parent goes first. A
    // failure anywhere leaves those rows local-only; resendLocalTodos sends
    // them (parents first) on the next refresh, focus or reconnect.
    const parentId = await sendCreate(parentRow);
    if (!parentId) return;
    for (const child of childRows) await sendCreate(child, parentId);
  }, [activeListId, sendCreate]);

  const addSubtask = useCallback((parentId: string, text: string) => {
    const clean = text.trim();
    if (!clean || isAppleId(parentId)) return;
    const tempId = nextLocalId();
    // The parent check uses current state inside the updater; the network
    // call stays outside it (updaters must be pure).
    setTodos(prev => {
      const parent = prev.find(t => t.id === parentId);
      if (!parent || parent.parentId) return prev; // one level max
      return [...prev, {
        id: tempId, listId: parent.listId, text: clean, completed: false,
        priority: 'Medium' as TodoPriority, parentId,
      }];
    });
    trackFeatureUsed('tasks');
    trackFeatureUsed('task_steps');
    // A local-only parent: its step is sent after it (resendLocalTodos).
    const parent = todosRef.current.find(t => t.id === parentId);
    if (!parent || parent.parentId || isLocalId(parentId) || isTmpListId(parent.listId)) return;
    if (!todosApi.isTodosApiConfigured()) return;
    void sendCreate({
      id: tempId, listId: parent.listId, text: clean, completed: false,
      priority: 'Medium', parentId,
    }, parentId);
  }, [sendCreate]);

  const toggleTodoCompleted = useCallback((id: string) => {
    if (isAppleId(id)) {
      const reminder = getAppleRemindersSnapshot().reminders.find(r => `apple:${r.id}` === id);
      if (reminder) setAppleReminderCompleted(id, !reminder.completed);
      return;
    }
    setTodos(prevTodos => {
      const target = prevTodos.find(t => t.id === id);
      const completing = target ? !target.completed : false;
      // §11.3: completing a PARENT completes its still-open steps (one gesture,
      // one cascade — disclosed in the disc's aria-label). Un-completing a
      // parent restores the parent only; children keep their own state.
      const cascadeIds = completing
        ? prevTodos.filter(t => t.parentId === id && !t.completed).map(t => t.id)
        : [];
      const affected = new Set([id, ...cascadeIds]);
      // Completion reaches the provider: a synced row shows `pending` (the
      // only syncState the client may set, §7a) until the write returns.
      const updated = prevTodos.map(todo =>
        affected.has(todo.id)
          ? {
            ...todo,
            completed: todo.id === id ? completing : true,
            ...(pushesToProvider(todo, { completed: true }) ? { syncState: 'pending' as const } : {}),
          }
          : todo,
      );
      log('Toggled todo', { id, cascaded: cascadeIds.length });

      if (todosApi.isTodosApiConfigured()) {
        for (const affectedId of affected) {
          if (isLocalId(affectedId)) continue;
          const todo = updated.find(t => t.id === affectedId);
          if (!todo) continue;
          const input = { id: affectedId, text: todo.text, completed: todo.completed, priority: todo.priority, blockKey: todo.blockKey };
          todosApi.upsertTodo(input)
            .then(row => setTodos(curr => curr.map(t => (t.id === affectedId ? withServerFields(t, row) : t))))
            .catch(() => {
              // Enqueue for replay when back online
              enqueuePending({ type: 'upsert', input });
            });
        }
      }

      return updated;
    });
  }, []);

  const updateTodo = useCallback((id: string, text: string, priority?: TodoPriority) => {
    if (text.trim() === '') return;
    if (isAppleId(id)) {
      updateAppleReminder(id, text, priority);
      return;
    }

    setTodos(prevTodos => {
      const updated = prevTodos.map(todo => {
        if (todo.id !== id) return todo;
        const change = { text: todo.text !== text, priority: Boolean(priority) && priority !== todo.priority };
        return {
          ...todo,
          text,
          ...(priority ? { priority } : {}),
          // A priority-only edit on a Google list stays local (§7c): never pending.
          ...(pushesToProvider(todo, change) ? { syncState: 'pending' as const } : {}),
        };
      });
      log('Updated todo', { id });

      if (todosApi.isTodosApiConfigured() && !isLocalId(id)) {
        const todo = updated.find(t => t.id === id);
        if (todo) {
          const input = { id, text: todo.text, completed: todo.completed, priority: todo.priority, blockKey: todo.blockKey };
          todosApi.upsertTodo(input)
            .then(row => setTodos(curr => curr.map(t => (t.id === id ? withServerFields(t, row) : t))))
            .catch(() => {
              enqueuePending({ type: 'upsert', input });
            });
        }
      }

      return updated;
    });
  }, []);

  // Schedule (or clear, with undefined) a task's prayer block — Barakah Blocks.
  const setTodoBlock = useCallback((id: string, blockKey?: string) => {
    // Reminders carry no prayer block (they never appear in Today).
    if (isAppleId(id)) return;
    setTodos(prevTodos => {
      const updated = prevTodos.map(todo =>
        todo.id === id ? { ...todo, blockKey } : todo,
      );
      log('Set todo block', { id });

      if (todosApi.isTodosApiConfigured() && !isLocalId(id)) {
        const todo = updated.find(t => t.id === id);
        if (todo) {
          // Explicit null clears the block server-side (undefined would leave it unchanged).
          const input = { id, text: todo.text, completed: todo.completed, priority: todo.priority, blockKey: blockKey ?? null };
          todosApi.upsertTodo(input)
            .catch(() => {
              enqueuePending({ type: 'upsert', input });
            });
        }
      }

      return updated;
    });
  }, []);

  const deleteTodo = useCallback((id: string) => {
    if (isAppleId(id)) {
      deleteAppleReminder(id);
      return;
    }
    setTodos(prevTodos => {
      log('Deleted todo', { id });

      if (todosApi.isTodosApiConfigured() && !isLocalId(id)) {
        todosApi.deleteTodo(id)
          .catch(() => {
            enqueuePending({ type: 'delete', id });
          });
      }

      // The DB cascade (Todo_parentId_fkey ON DELETE CASCADE) removes the
      // children server-side — mirror it locally or the steps linger as
      // orphaned top-level rows that error on edit and vanish on next sync.
      return prevTodos.filter(todo => todo.id !== id && todo.parentId !== id);
    });
  }, []);

  const toggleShowTodos = useCallback(() => toggleOverlay('todos'), [toggleOverlay]);
  const toggleShowNotes = useCallback(() => toggleOverlay('notes'), [toggleOverlay]);
  // Preserves the feature_used:qibla analytics on open (handled in toggleOverlay).
  const toggleQibla = useCallback(() => toggleOverlay('qibla'), [toggleOverlay]);

  // Every call here is a deliberate user edit, so it stamps provenance: from now on
  // the name renders exactly as typed and no derivation (provider displayName, email
  // prefix, first-name reduction) may rewrite it. Clearing the name clears the flag,
  // which re-enables the derived seed — that is the only way back to automatic.
  const setName = useCallback((nextName: string) => {
    const trimmed = nextName.trim();
    setNameState(trimmed);
    setNameIsCustomState(trimmed.length > 0);
  }, []);

  const setFocusValue = useCallback((nextFocus: string) => {
    setFocus(nextFocus);
    if (!nextFocus.trim()) {
      setFocusCompletedState(false);
    }
  }, []);

  // Hydrate the daily intention (`focus`) AND the greeting name from the backend
  // on login so they follow the user across devices. Mirrors the todo-lists sync
  // above: ref-guarded to run once per uid, with the backend treated as source of
  // truth at login. Applying the remote focus (including "") means removing the
  // intention on one device propagates to the others. displayName is only applied
  // when non-empty: an account that never set a name must not wipe the
  // Firebase-displayName seed below.
  useEffect(() => {
    if (!user?.uid) { focusSyncedForUserRef.current = null; return; }
    if (!profileGraphqlApi.isProfileGraphqlConfigured()) return;
    if (focusSyncedForUserRef.current === user.uid) return;
    const uid = user.uid;
    let cancelled = false;

    (async () => {
      try {
        const profile = await profileGraphqlApi.getProfile();
        if (cancelled) return;
        focusSyncedForUserRef.current = uid;
        if (profile) {
          if (typeof profile.focus === 'string') setFocusValue(profile.focus);
          // Account-truth name: a custom name typed on another device (or before a
          // sign-out on this one) comes back here and wins over the Firebase seed.
          // Provenance travels with it — an account carrying nameIsCustom adopts both
          // the flag and the exact name, so there is no first-name reduction here and
          // no re-derivation on later renders. Without the flag the legacy heuristic
          // still applies: remote === provider displayName means provider-derived.
          const remoteIsCustom = (profile.preferences as { nameIsCustom?: unknown } | null)
            ?.nameIsCustom === true;
          const remoteName = normalizeRemoteName(profile.displayName, user.displayName, remoteIsCustom);
          if (remoteName) {
            setNameState(remoteName);
            if (remoteIsCustom) setNameIsCustomState(true);
          }
        }
      } catch (e) {
        log('Failed to sync focus from backend:', e as Error);
      } finally {
        // Settled (success OR failure) — opens the prefs-push gate below. On
        // failure we still open it: offline-first users must be able to sync
        // their edits; only the pre-hydration window is fenced off.
        if (!cancelled) setRemoteProfileSettledUid(uid);
      }
    })();

    return () => { cancelled = true; };
    // user?.displayName feeds normalizeRemoteName; re-runs for the same uid are
    // no-ops via the ref guard above.
  }, [user?.uid, user?.displayName, setFocusValue]);

  const setFocusCompleted = useCallback((completed: boolean) => {
    setFocusCompletedState(completed);
    stampPrefsUpdatedAt();
  }, []);

  const setPrayerCalculationMethod = useCallback((method: number) => {
    if (!Number.isFinite(method)) return;
    setPrayerCalculationMethodState(Math.max(1, Math.min(20, Math.trunc(method))));
    stampPrefsUpdatedAt();
  }, []);

  const setWeatherUnit = useCallback((unit: 'F' | 'C') => {
    if (unit !== 'F' && unit !== 'C') return;
    setWeatherUnitState(unit);
    stampPrefsUpdatedAt();
  }, []);

  // Hydrate name from auth profile for first-time users who have not set one
  // yet. Provider names are reduced to the FIRST name ("Ergi Shehu" → "Ergi")
  // — founder directive, TestFlight build 7. Also self-heals a stale full-name
  // seed persisted by builds that pre-date the reduction (name === full
  // provider displayName), which the backend hydration alone can't fix on
  // offline or backend-unconfigured devices. Idempotent: once reduced, name no
  // longer equals the full displayName and this effect no-ops.
  useEffect(() => {
    if (!user) return;
    // A user-set name is authoritative: never re-seed or first-name-reduce it.
    if (nameIsCustom) return;

    const profileName = user.displayName?.trim();
    if (profileName) {
      const first = firstNameOf(profileName);
      if (!name) {
        setNameState(first);
      } else if (name.trim() === profileName && first !== name.trim()) {
        setNameState(first);
      }
      return;
    }

    if (name) return;
    const emailPrefix = user.email?.split('@')[0]?.trim();
    if (emailPrefix) {
      setNameState(emailPrefix);
    }
  }, [name, user, nameIsCustom]);

  // Hydrate profile from backend once per signed-in user.
  useEffect(() => {
    if (!user?.uid) {
      hydratedProfileUserRef.current = null;
      profileHydratedRef.current = false;
      lastSyncedProfileRef.current = '';
      return;
    }

    if (!profileApi.isProfileApiConfigured()) {
      profileHydratedRef.current = true;
      return;
    }

    if (hydratedProfileUserRef.current === user.uid) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const [profile, localPrefsAtStr] = await Promise.all([
          profileApi.getMyProfile(),
          loadFromStorageAsync<string | null>(PREFS_LOCAL_UPDATED_AT_KEY, null),
        ]);
        if (cancelled) return;

        // Last-write-wins: remote preferences only applied when remote is newer.
        const remotePrefsAt = typeof profile.updatedAt === 'string' ? new Date(profile.updatedAt).getTime() : 0;
        const localPrefsAt = localPrefsAtStr ? new Date(localPrefsAtStr).getTime() : 0;
        const remotePrefsWins = remotePrefsAt > localPrefsAt;

        hydratedProfileUserRef.current = user.uid;
        profileHydratedRef.current = true;

        if (typeof profile.displayName === 'string') {
          // Same reduction contract as the GraphQL hydration above: provider-
          // derived full names → first name, user-typed names verbatim.
          setNameState(normalizeRemoteName(profile.displayName, user.displayName));
        }
        if (remotePrefsWins && typeof profile.focus === 'string') {
          setFocus(profile.focus);
        }
        if (Array.isArray(profile.todos)) {
          setTodos(profile.todos.map(normalizeTodo));
        }

        const prefMethod = Number((profile.preferences as any)?.prayerCalculationMethod);
        const prefFocusCompleted = Boolean((profile.preferences as any)?.focusCompleted);
        const prefWeatherUnit = (profile.preferences as any)?.weatherUnit;

        if (remotePrefsWins) {
          if (Number.isFinite(prefMethod)) {
            setPrayerCalculationMethodState(Math.max(1, Math.min(20, Math.trunc(prefMethod))));
          }
          if (prefWeatherUnit === 'F' || prefWeatherUnit === 'C') {
            setWeatherUnitState(prefWeatherUnit);
          }
          setFocusCompletedState(prefFocusCompleted);
          log('Remote preferences applied (remote newer)', { remotePrefsAt, localPrefsAt });
        } else {
          log('Local preferences kept (local newer or equal)', { remotePrefsAt, localPrefsAt });
        }

        const payloadSnapshot = JSON.stringify({
          displayName: typeof profile.displayName === 'string' ? profile.displayName.trim() : '',
          focus: typeof profile.focus === 'string' ? profile.focus : '',
          todos: Array.isArray(profile.todos) ? profile.todos : [],
          preferences: {
            prayerCalculationMethod: remotePrefsWins && Number.isFinite(prefMethod) ? Math.max(1, Math.min(20, Math.trunc(prefMethod))) : prayerCalculationMethod,
            weatherUnit: remotePrefsWins && (prefWeatherUnit === 'F' || prefWeatherUnit === 'C') ? prefWeatherUnit : weatherUnit,
            focusCompleted: remotePrefsWins ? prefFocusCompleted : focusCompleted,
          },
        });
        lastSyncedProfileRef.current = payloadSnapshot;
      } catch (e) {
        profileHydratedRef.current = true;
        log('Failed to hydrate profile from backend:', e as Error);
      }
    })();

    return () => {
      cancelled = true;
    };
    // user?.displayName feeds normalizeRemoteName; hydratedProfileUserRef makes
    // re-runs for the same uid no-ops.
  }, [user?.uid, user?.displayName, prayerCalculationMethod, weatherUnit, focusCompleted]);

  // Sync preferences to the GraphQL backend (debounced, independent of legacy profileApi).
  useEffect(() => {
    if (!user?.uid) {
      lastSyncedPrefsRef.current = '';
      return;
    }
    if (!profileGraphqlApi.isProfileGraphqlConfigured()) return;
    // Hydration-before-push: until this account's own remote profile has been
    // fetched (or definitively failed), pushing would overwrite it with this
    // device's pre-hydration local state — on slow networks that wiped the new
    // account's focus/prefs with defaults right after an account switch.
    if (remoteProfileSettledUid !== user.uid) return;

    const payload: Record<string, unknown> = {
      prayerCalculationMethod,
      weatherUnit,
      focusCompleted,
      nameIsCustom,
    };
    const trimmedFocus = focus.trim();
    const trimmedName = name.trim();
    // Change-detection MUST include focus + name, otherwise a focus-only edit (or a
    // clear) never triggers a write and the GraphQL backend keeps a stale value — which
    // the login hydration would then re-apply over the user's change.
    const snapshot = JSON.stringify({ ...payload, focus: trimmedFocus, name: trimmedName });
    if (snapshot === lastSyncedPrefsRef.current) return;

    const timeoutId = window.setTimeout(() => {
      profileGraphqlApi.updateProfile({
        // Same rule as focus below: send the empty string, never undefined. With
        // `|| undefined` a cleared name never reached the backend, so the old custom
        // displayName survived remotely and hydration re-applied it on the next
        // launch — clearing back to the derived name could not stick.
        displayName: trimmedName,
        // Send the empty string (not undefined) so clearing the intention is persisted —
        // `trim() || undefined` dropped clears, so removal never reached the backend.
        focus: trimmedFocus,
        preferences: payload,
      })
        .then(() => { lastSyncedPrefsRef.current = snapshot; })
        .catch((e) => {
          log('Failed to sync preferences to GraphQL backend:', e as Error);
          showToast('Settings failed to save. Check your connection.', 'warning');
        });
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, [user?.uid, remoteProfileSettledUid, name, nameIsCustom, focus, prayerCalculationMethod, weatherUnit, focusCompleted, showToast]);

  // Persist profile state changes to backend for future APIs.
  useEffect(() => {
    if (!user?.uid) return;
    if (!profileApi.isProfileApiConfigured()) return;
    if (!profileHydratedRef.current) return;

    const payload = {
      displayName: name.trim(),
      focus: focus.trim(),
      todos,
      preferences: {
        prayerCalculationMethod,
        weatherUnit,
        focusCompleted,
      },
    };
    const payloadSnapshot = JSON.stringify(payload);
    if (payloadSnapshot === lastSyncedProfileRef.current) return;

    const timeoutId = window.setTimeout(() => {
      void profileApi.updateMyProfile(payload)
        .then(() => {
          lastSyncedProfileRef.current = payloadSnapshot;
        })
        .catch((e) => {
          log('Failed to save profile to backend:', e as Error);
        });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [user?.uid, name, focus, todos, prayerCalculationMethod, weatherUnit, focusCompleted]);

  // ── Apple Reminders (native only): shown beside the server-backed lists ──
  // Kept OUT of `todos` / `todoLists` state on purpose: those are persisted,
  // synced and sent in the profile payload; Reminders stay on the device.
  // Per account: a sign-out reset them, so the next account re-reads its own.
  useEffect(() => {
    installAppleReminders();
    void reloadAppleReminders();
  }, [user?.uid]);
  const apple = useSyncExternalStore(subscribeAppleReminders, getAppleRemindersSnapshot, getAppleRemindersSnapshot);
  const visibleTodos = useMemo(
    () => (apple.reminders.length ? [...todos, ...appleTodos(apple)] : todos),
    [todos, apple],
  );
  const visibleLists = useMemo(
    () => (apple.lists.length ? [...todoLists, ...appleTodoLists(apple)] : todoLists),
    [todoLists, apple],
  );

  const value: AppContextType = {
    name,
    setName,
    nameIsCustom,
    focus,
    setFocus: setFocusValue,
    focusCompleted,
    setFocusCompleted,
    prayerCalculationMethod,
    setPrayerCalculationMethod,
    weatherUnit,
    setWeatherUnit,
    todos: visibleTodos,
    addTodo,
    setTodoBlock,
    toggleTodoCompleted,
    updateTodo,
    deleteTodo,
    todoLists: visibleLists,
    activeListId,
    setActiveListId,
    addTodoTree,
    addSubtask,
    createList,
    listCreates,
    retryListCreate,
    keepListInRhemn,
    renameList,
    deleteList,
    refreshFromServer,
    retryTodoSync,
    moveTodo,
    appleRemindersOff: apple.accessOff,
    activeOverlay,
    openOverlay,
    todosView,
    setTodosView,
    openTodos,
    focusRequest,
    requestFocusSession,
    clearFocusRequest,
    closeOverlay,
    toggleOverlay,
    showTodos,
    toggleShowTodos,
    showNotes,
    toggleShowNotes,
    showQibla,
    toggleQibla,
    showFavorites,
    toggleShowFavorites: useCallback(() => toggleOverlay('favorites'), [toggleOverlay]),
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
